import type {
  DigestPreferencesEditorResponseDto,
  ManageDigestResponseDto,
  PatchDigestPreferencesResponseDto,
} from '@nomiss/sdk';
import { executeSdkRawRequest, parseApiErrorResponse } from './api-request.util';
import {
  fetchWithAuthRecovery,
  onboardingApi,
  resolveAccessToken,
} from './sdk-client';

export type ManageDigestPreferencePill = {
  id: string;
  label: string;
  imageUrl?: string;
};

export type ManageDigestPreferenceSection = {
  stepId: string;
  label: { text?: string; key?: string };
  pills: ManageDigestPreferencePill[];
};

export type ManageDigestSportPreferences = {
  sportCode: string;
  hidden: boolean;
  sections: ManageDigestPreferenceSection[];
};

export type ManageDigestPreferencesResponse = {
  sportCodes: string[];
  hiddenSportCodes: string[];
  sports: ManageDigestSportPreferences[];
};

function normalizeManageDigestResponse(
  payload: ManageDigestResponseDto,
): ManageDigestPreferencesResponse {
  return {
    sportCodes: Array.isArray(payload?.sportCodes) ? payload.sportCodes : [],
    hiddenSportCodes: Array.isArray(payload?.hiddenSportCodes)
      ? payload.hiddenSportCodes
      : [],
    sports: Array.isArray(payload?.sports)
      ? payload.sports.map(sport => ({
          sportCode: sport.sportCode,
          hidden: Boolean(sport.hidden),
          sections: (sport.sections ?? []).map(section => ({
            stepId: section.stepId,
            label: section.label ?? {},
            pills: (section.pills ?? []).map(pill => ({
              id: pill.id,
              label: pill.label,
              imageUrl: pill.imageUrl,
            })),
          })),
        }))
      : [],
  };
}

export async function getManageDigestPreferences(
  accessToken?: string | null,
): Promise<ManageDigestPreferencesResponse> {
  const token = accessToken ?? resolveAccessToken();

  const initOverrides: RequestInit | undefined = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : undefined;

  const data = await executeSdkRawRequest<ManageDigestResponseDto>({
    request: () =>
      onboardingApi.onboardingControllerGetMyDigestPreferencesV1Raw(
        {},
        initOverrides,
      ),
    fallback: 'Manage digest API failed',
  });

  return normalizeManageDigestResponse(data);
}

export type DigestPreferencesEditorResponse = {
  displayName: string;
  selectedSports: string[];
  sportAnswers: Record<string, Record<string, string[]>>;
  sportsNeedingSetup: string[];
};

export type PatchDigestPreferencesInput = {
  selectedSports: string[];
  sportAnswers: Record<string, Record<string, string[]>>;
};

export type PatchDigestPreferencesResponse = {
  savedAt: string;
  sportCodes: string[];
};

export async function getDigestPreferencesEditor(
  accessToken?: string | null,
): Promise<DigestPreferencesEditorResponse> {
  const token = accessToken ?? resolveAccessToken();

  const initOverrides: RequestInit | undefined = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : undefined;

  const data = await executeSdkRawRequest<DigestPreferencesEditorResponseDto>({
    request: () =>
      onboardingApi.onboardingControllerGetMyDigestPreferencesEditorV1Raw(
        initOverrides,
      ),
    fallback: 'Digest preferences editor API failed',
  });

  return {
    displayName: data.displayName ?? '',
    selectedSports: Array.isArray(data.selectedSports)
      ? data.selectedSports
      : [],
    sportAnswers: (data.sportAnswers ?? {}) as Record<
      string,
      Record<string, string[]>
    >,
    sportsNeedingSetup: Array.isArray(data.sportsNeedingSetup)
      ? data.sportsNeedingSetup
      : [],
  };
}

function extractSportCode(entry: unknown): string | null {
  if (typeof entry === 'string' && entry.length > 0) {
    return entry;
  }

  if (entry && typeof entry === 'object' && 'code' in entry) {
    const code = (entry as { code?: unknown }).code;
    if (typeof code === 'string' && code.length > 0) {
      return code;
    }
  }

  return null;
}

function normalizeSelectedSportsInput(
  body: PatchDigestPreferencesInput,
): string[] {
  const raw = body.selectedSports;
  const fromSelected = Array.isArray(raw)
    ? raw
        .map(extractSportCode)
        .filter((code): code is string => code != null)
    : extractSportCode(raw)
      ? [extractSportCode(raw)!]
      : [];

  if (fromSelected.length > 0) {
    return [...new Set(fromSelected)];
  }

  if (
    body.sportAnswers &&
    typeof body.sportAnswers === 'object' &&
    !Array.isArray(body.sportAnswers)
  ) {
    return Object.keys(body.sportAnswers).filter(key => key.length > 0);
  }

  return [];
}

export async function patchDigestPreferences(
  body: PatchDigestPreferencesInput,
  accessToken?: string | null,
): Promise<PatchDigestPreferencesResponse> {
  const token = accessToken ?? resolveAccessToken();

  const initOverrides: RequestInit | undefined = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : undefined;

  const normalizedBody: PatchDigestPreferencesInput = {
    selectedSports: normalizeSelectedSportsInput(body),
    sportAnswers:
      body.sportAnswers &&
      typeof body.sportAnswers === 'object' &&
      !Array.isArray(body.sportAnswers)
        ? body.sportAnswers
        : {},
  };

  const data = await executeSdkRawRequest<PatchDigestPreferencesResponseDto>({
    request: () =>
      onboardingApi.onboardingControllerPatchMyDigestPreferencesV1Raw(
        { patchDigestPreferencesDto: normalizedBody },
        initOverrides,
      ),
    fallback: 'Patch digest preferences API failed',
  });

  return {
    savedAt: data.savedAt ?? new Date().toISOString(),
    sportCodes: Array.isArray(data.sportCodes) ? data.sportCodes : [],
  };
}

interface ApiEnvelope<T> {
  data: T;
}

async function manageDigestRequest<T>(
  path: string,
  init?: RequestInit,
  accessToken?: string | null,
): Promise<T> {
  const { resolveAccessToken, buildApiHeaders } = await import('./sdk-client');

  const token = accessToken ?? resolveAccessToken();
  const headers = buildApiHeaders({
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  });

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const { apiConfig } = await import('@nomiss/configs');
  const response = await fetchWithAuthRecovery(`${apiConfig.baseUrl}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    throw await parseApiErrorResponse(
      response,
      'Unable to save your digest preferences. Please try again.',
    );
  }

  const json = (await response.json()) as ApiEnvelope<T> | T;
  return 'data' in (json as ApiEnvelope<T>) ? (json as ApiEnvelope<T>).data : (json as T);
}

export type PatchDigestStepInput = {
  selection: string[];
};

export type PatchDigestStepResponse = {
  savedAt: string;
  sportCodes: string[];
  sportCode: string;
  stepId: string;
  selection: string[];
};

export async function patchDigestPreferenceStep(
  sportCode: string,
  stepId: string,
  body: PatchDigestStepInput,
  accessToken?: string | null,
): Promise<PatchDigestStepResponse> {
  return manageDigestRequest<PatchDigestStepResponse>(
    `/v1/onboarding/digest-preferences/me/sports/${encodeURIComponent(sportCode)}/steps/${encodeURIComponent(stepId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
    accessToken,
  );
}

export type RemoveDigestSportResponse = {
  savedAt: string;
  sportCodes: string[];
  removedSportCode: string;
};

export async function removeDigestSport(
  sportCode: string,
  accessToken?: string | null,
): Promise<RemoveDigestSportResponse> {
  return manageDigestRequest<RemoveDigestSportResponse>(
    `/v1/onboarding/digest-preferences/me/sports/${encodeURIComponent(sportCode)}`,
    { method: 'DELETE' },
    accessToken,
  );
}

export type PatchDigestSportVisibilityInput = {
  hidden: boolean;
};

export type PatchDigestSportVisibilityResponse = {
  savedAt: string;
  sportCodes: string[];
  hiddenSportCodes: string[];
  sportCode: string;
  hidden: boolean;
};

export async function patchDigestSportVisibility(
  sportCode: string,
  body: PatchDigestSportVisibilityInput,
  accessToken?: string | null,
): Promise<PatchDigestSportVisibilityResponse> {
  return manageDigestRequest<PatchDigestSportVisibilityResponse>(
    `/v1/onboarding/digest-preferences/me/sports/${encodeURIComponent(sportCode)}/visibility`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
    accessToken,
  );
}
